import React from 'react';
import { Link } from 'react-router-dom';

const Privacy = () => {
    return (
        <div className="container mx-auto px-4 py-12 max-w-4xl">
            {/* Header */}
            <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-gradpath-green to-gradpath-dark rounded-2xl mb-4">
                    <i className="fas fa-shield-alt text-white text-2xl"></i>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gradpath-dark mb-3">
                    Privacy Policy
                </h1>
                <p className="text-gray-600">
                    How we protect and handle your information
                </p>
                <p className="text-sm text-gray-500 mt-2">
                    Last updated: June 1, 2026
                </p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 space-y-6">
                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-info-circle text-gradpath-green text-lg"></i>
                        Information We Collect
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We collect personal information including your name, email address, ID number, phone number, 
                        educational background, and job application history. This information is provided voluntarily 
                        when you create an account or use our services.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-chart-line text-gradpath-green text-lg"></i>
                        How We Use Your Information
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Your information is used to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Provide job matching and recommendation services</li>
                        <li>Track your job applications and history</li>
                        <li>Improve our platform and user experience</li>
                        <li>Send you relevant job alerts and career advice</li>
                        <li>Analyze demographics and usage patterns</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        <strong className="text-gradpath-green">We never sell your personal data</strong> to third parties.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-lock text-gradpath-green text-lg"></i>
                        Data Security
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We implement industry-standard security measures to protect your data, including:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li>Encryption of sensitive data</li>
                        <li>Secure Firebase Authentication</li>
                        <li>Regular security audits</li>
                        <li>Secure servers with firewalls</li>
                        <li>Limited employee access to user data</li>
                    </ul>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-cookie-bite text-gradpath-green text-lg"></i>
                        Cookies & Tracking
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We use cookies to remember your login session, analyze site traffic, and personalize your experience. 
                        You can disable cookies in your browser settings, but this may affect some functionality.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-user-shield text-gradpath-green text-lg"></i>
                        Your Rights
                    </h2>
                    <p className="text-gray-700 leading-relaxed mb-3">
                        Under South African law (POPIA), you have the right to:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                        <li>Access your personal information</li>
                        <li>Correct inaccurate or incomplete information</li>
                        <li>Request deletion of your data</li>
                        <li>Object to processing of your data</li>
                        <li>Data portability</li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        You can exercise these rights through your account settings or by contacting us directly.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-child text-gradpath-green text-lg"></i>
                        Children's Privacy
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        Our services are intended for users 18 years and older. We do not knowingly collect 
                        information from individuals under 18 years of age.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-globe text-gradpath-green text-lg"></i>
                        Third-Party Services
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        We use trusted third-party services including:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
                        <li><strong>Google</strong> - Authentication, database, and storage</li>
                        <li><strong>Google Analytics</strong> - Site usage analytics </li>
                    </ul>
                    <p className="text-gray-700 leading-relaxed mt-3">
                        These services have their own privacy policies and data processing agreements.
                    </p>
                </section>

                <section>
                    <h2 className="text-xl font-bold text-gradpath-dark mb-3 flex items-center gap-2">
                        <i className="fas fa-envelope text-gradpath-green text-lg"></i>
                        Contact Us
                    </h2>
                    <p className="text-gray-700 leading-relaxed">
                        If you have questions about this Privacy Policy or how we handle your data, please contact us:
                    </p>
                    <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                        <p className="text-gray-700">
                            <i className="fas fa-envelope text-gradpath-green mr-2"></i>
                            <a href="mailto:support@gradpath.co.za" className="text-gradpath-green hover:underline">
                                support@gradpath.co.za
                            </a>
                        </p>
                        <p className="text-gray-700 mt-2">
                            <i className="fas fa-map-marker-alt text-gradpath-green mr-2"></i>
                            South Africa
                        </p>
                    </div>
                </section>

                <section className="bg-gray-50 p-4 rounded-lg">
                    <h2 className="text-lg font-bold text-gradpath-dark mb-2 flex items-center gap-2">
                        <i className="fas fa-sync-alt text-gradpath-green"></i>
                        Updates to This Policy
                    </h2>
                    <p className="text-gray-700 text-sm">
                        We may update this Privacy Policy from time to time. We will notify you of any material changes 
                        via email or through a notice on our website. Your continued use of GradPath after such updates 
                        constitutes acceptance of the revised policy.
                    </p>
                </section>

                {/* Back to Home Link */}
                <div className="text-center pt-4">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-gradpath-green hover:underline"
                    >
                        <i className="fas fa-arrow-left"></i>
                        Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Privacy;